import { useCallback, useEffect, useState } from "react";
import Cookies from "js-cookie";

import { API_PROJECT_WORKSTREAMS } from "../../../config/Endpoints.js";
import { deleteApi, getApi, postApi, putApi } from "../../utils/api.ts";
import type { Workstream, WorkstreamInput } from "./types.ts";

type WorkstreamsResponse = {
  data?: Workstream[];
};

function unwrapWorkstreams(raw: unknown): Workstream[] {
  if (Array.isArray(raw)) return raw as Workstream[];
  if (raw && typeof raw === "object" && Array.isArray((raw as WorkstreamsResponse).data)) {
    return (raw as WorkstreamsResponse).data ?? [];
  }
  return [];
}

export function useWorkstreams(projectId: string) {
  const [workstreams, setWorkstreams] = useState<Workstream[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const accessToken = Cookies.get("accessToken");

  const fetchWorkstreams = useCallback(async (signal?: AbortSignal) => {
    if (!projectId) {
      setWorkstreams([]);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await getApi<unknown>(
        `${API_PROJECT_WORKSTREAMS.replace(/\/workstreams$/, "")}/${projectId}/workstreams`,
        accessToken,
        "json",
        signal,
      );
      if (!signal?.aborted) setWorkstreams(unwrapWorkstreams(response.data));
    } catch (e) {
      if (e instanceof Error && e.name === "AbortError") return;
      if (!signal?.aborted) {
        setError(e instanceof Error ? e.message : "Failed to load workstreams");
        setWorkstreams([]);
      }
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [accessToken, projectId]);

  useEffect(() => {
    const controller = new AbortController();
    void fetchWorkstreams(controller.signal);
    return () => controller.abort();
  }, [fetchWorkstreams]);

  const saveWorkstreams = async (items: WorkstreamInput[], editing: boolean) => {
    const body = { projectId: Number(projectId), workstreams: items };
    if (editing) {
      await putApi(API_PROJECT_WORKSTREAMS, accessToken, body);
    } else {
      await postApi(API_PROJECT_WORKSTREAMS, accessToken, body);
    }
    await fetchWorkstreams();
  };

  const removeWorkstream = async (id: number) => {
    await deleteApi(`${API_PROJECT_WORKSTREAMS}/${id}`, accessToken);
    await fetchWorkstreams();
  };

  return {
    workstreams,
    loading,
    error,
    refetch: () => fetchWorkstreams(),
    saveWorkstreams,
    removeWorkstream,
  };
}
